/**
 * The console's Users tab: the roster an operator browses, and the one control that decides who
 * else may operate this deployment.
 *
 * Drawn only for an admin, and that is a convenience rather than the access control — every
 * procedure behind it (`admin.users.list`, `.detail`, `.setRole`) is `adminProcedure` on the
 * server, so a moderator who reached this component would get a panel of refusals rather than a
 * roster. The Ban button is the exception in the other direction: `admin.moderation.banUser` is
 * `moderatorProcedure`, and it is here because an admin looking at an account is exactly where a
 * ban gets decided.
 *
 * ## One list with filters, rather than a second list
 *
 * The report asked for "a list of players currently online" beside the roster. That is this
 * list with `activity=online` selected, not a panel of its own: two lists would be two places to
 * fix a column, two definitions of a guest and two answers to "how many accounts are there".
 * Every filter is server-side — the page count and the range under the table are counts over the
 * *filtered* set, which a client-side filter could not give.
 *
 * **THERE ARE NO BOTS IN HERE.** Not because anything filters them out: this is a query over
 * `users`, and a bot has no row there. See the note at the top of `admin-users.ts`.
 *
 * Two things the page has to say out loud, because both are surprising if you meet them by
 * accident:
 *
 *  - a **guest** cannot hold a role at all (a guest row is reclaimed by a device id anybody can
 *    send), so its role cell is a sentence rather than a disabled dropdown nobody can explain;
 *  - an address in **`ADMIN_EMAILS`** is granted `admin` again on its next sign-in whatever is
 *    picked here, so a listed row says so beside the role. The fix is the env var, not the click.
 */

import { PAGINATION } from '@chompdome/shared/constants';
import { useState } from 'react';
import type {
  AdminUserDeletion,
  AdminUserRow,
  AdminUserSort,
  AdminUserSortDirection,
} from '../../../../server/src/trpc/routers/admin-users.js';
import { type BanTarget, BanDialog } from '@/components/admin/ban-dialog';
import {
  type DeleteAccountTarget,
  DeleteAccountDialog,
} from '@/components/admin/delete-account-dialog';
import { Instant } from '@/components/admin/instant';
import { UserDetailPanel } from '@/components/admin/user-detail-panel';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';

/** Mirrors `USER_ROLES` in the server's `db/schema/users.ts`; the column is what enforces it. */
const ROLE_OPTIONS = [
  { value: 'player', label: 'Player' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'admin', label: 'Admin' },
] as const;

type Role = (typeof ROLE_OPTIONS)[number]['value'];

/** The role filter: the three roles, plus everybody. */
const ROLE_FILTERS = [{ value: 'any', label: 'Any role' }, ...ROLE_OPTIONS] as const;

const TYPE_FILTERS = [
  { value: 'any', label: 'Guests and accounts' },
  { value: 'registered', label: 'Registered only' },
  { value: 'guest', label: 'Guests only' },
] as const;

const BAN_FILTERS = [
  { value: 'any', label: 'Any ban state' },
  { value: 'banned', label: 'Banned' },
  { value: 'clear', label: 'Not banned' },
] as const;

/**
 * The activity filter, and the one place in this client that has to say what "online" means.
 *
 * `online` is a live game connection somewhere in the fleet — the server decides it in
 * `services/presence` and this page never asks a host anything. The three windows under it are
 * `users.last_seen_at`, which is throttled to one write per player per five minutes, so they are
 * labelled "seen in…" rather than "online in…": a player reading the lobby is seen, not playing.
 */
const ACTIVITY_FILTERS = [
  { value: 'any', label: 'Any time' },
  { value: 'online', label: 'Playing now' },
  { value: 'hour', label: 'Seen in the last hour' },
  { value: 'day', label: 'Seen in the last 24 hours' },
  { value: 'week', label: 'Seen in the last 7 days' },
] as const;

/** Rows per page. Every value is at or under the API's `PAGINATION.MAX_LIMIT`. */
const PAGE_SIZES = [10, PAGINATION.DEFAULT_LIMIT, PAGINATION.MAX_LIMIT] as const;

/**
 * The five sortable columns, and which way a first click on each one points.
 *
 * **THE SORT IS THE SERVER'S.** Every one of these is a field on `admin.users.list`, because the
 * list is OFFSET-paginated: ordering the 25 rows this page happens to hold would be wrong about
 * every other page, and wrong about which rows landed on this one. Nothing here sorts anything.
 *
 * The initial direction is what somebody means by clicking the column: a date is asked for
 * newest-first, and a name, a kind and a role are asked for from the top of the alphabet. A
 * second click on the same column reverses it.
 *
 * `Activity` says **(last seen)** out loud, and that is the honest half rather than decoration:
 * the cell draws "Playing now" for a live socket, and presence is a fan-out the server computes
 * after the query — so what the column can be *ordered* by is `last_seen_at` and nothing else.
 * Moderation and Delete hold actions rather than values, and are not sortable at all.
 */
const SORTABLE_COLUMNS = [
  { key: 'account', label: 'Account', initial: 'asc' },
  { key: 'type', label: 'Type', initial: 'asc' },
  { key: 'activity', label: 'Activity', note: 'last seen', initial: 'desc' },
  { key: 'created', label: 'Created', initial: 'desc' },
  { key: 'role', label: 'Role', initial: 'asc' },
] as const satisfies readonly {
  key: AdminUserSort;
  label: string;
  note?: string;
  initial: AdminUserSortDirection;
}[];

type Sort = { key: AdminUserSort; direction: AdminUserSortDirection };

/** What the procedure defaults to, said again here so the header can draw it before any click. */
const DEFAULT_SORT: Sort = { key: 'created', direction: 'desc' };

type Filters = {
  role: (typeof ROLE_FILTERS)[number]['value'];
  accountType: (typeof TYPE_FILTERS)[number]['value'];
  banned: (typeof BAN_FILTERS)[number]['value'];
  activity: (typeof ACTIVITY_FILTERS)[number]['value'];
};

const DEFAULT_FILTERS: Filters = {
  role: 'any',
  accountType: 'any',
  banned: 'any',
  activity: 'any',
};

export function UsersPanel({ canDelete }: { canDelete: boolean }) {
  const utils = trpc.useUtils();
  // The applied search, and what is in the box. They differ while somebody is typing: searching
  // on every keystroke would put a query per character on a list nobody is watching change.
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
  const [limit, setLimit] = useState<number>(PAGINATION.DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);

  /** The account whose detail page is open, or null for the list. */
  const [openUserId, setOpenUserId] = useState<string | null>(null);

  /** The account the ban dialog is pointed at, or null. The dialog itself is `BanDialog`. */
  const [banning, setBanning] = useState<BanTarget | null>(null);

  /** The account the delete dialog is pointed at, or null. Opened from a row or the detail. */
  const [deleting, setDeleting] = useState<DeleteAccountTarget | null>(null);
  /**
   * What the last deletion answered, kept after the dialog closes.
   *
   * The row it was about is gone from the list, so the two things the server can only say *at*
   * the deletion — that the address is still on the allowlist, and that the row was a guest —
   * have nowhere else to be said. Dismissed by the operator, never on a timer.
   */
  const [deleted, setDeleted] = useState<AdminUserDeletion | null>(null);

  const page = trpc.admin.users.list.useQuery({
    search,
    ...filters,
    sort: sort.key,
    direction: sort.direction,
    offset,
    limit,
  });
  const setRole = trpc.admin.users.setRole.useMutation({
    onSuccess: async () => {
      await utils.admin.users.list.invalidate();
    },
  });
  // The same mutation the Moderation tab issues bans through, and the same rules on the server.
  // It is here as well because an admin looking at the roster is exactly where a ban is decided.
  const banUser = trpc.admin.moderation.banUser.useMutation({
    onSuccess: async () => {
      setBanning(null);
      await Promise.all([
        utils.admin.users.list.invalidate(),
        utils.admin.moderation.listBans.invalidate(),
      ]);
    },
  });
  const liftBan = trpc.admin.moderation.liftBan.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.admin.users.list.invalidate(),
        utils.admin.moderation.listBans.invalidate(),
      ]);
    },
  });
  /**
   * The one irreversible mutation on this console, and the only one that can be fired from the
   * detail view *about the account the detail view is drawing*. So success closes the dialog and
   * returns to the list before anything is invalidated: there is no account left to look at, and
   * a detail query re-run against a deleted id would answer NOT_FOUND.
   */
  const removeUser = trpc.admin.users.remove.useMutation({
    onSuccess: async (result) => {
      setDeleting(null);
      setOpenUserId(null);
      setDeleted(result);
      await Promise.all([
        utils.admin.users.list.invalidate(),
        utils.admin.users.detail.invalidate(),
        utils.admin.moderation.listBans.invalidate(),
      ]);
    },
  });

  /** Any change to what is being asked for is a new list; page 3 of the old one means nothing. */
  const applySearch = (next: string) => {
    setSearch(next);
    setOffset(0);
  };
  const applyFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setOffset(0);
  };
  /**
   * A click on a header: reverse the column that is already sorted, or point at a new one the
   * way that column is usually wanted. Back to page one either way — page 3 of one order names
   * different accounts than page 3 of another, so keeping the offset would land the operator
   * somewhere they did not ask to be.
   */
  const applySort = (key: AdminUserSort) => {
    const column = SORTABLE_COLUMNS.find((entry) => entry.key === key);
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: column?.initial ?? 'asc' },
    );
    setOffset(0);
  };

  /**
   * One dialog for both places a deletion is decided — a row, and the detail view — because it
   * is the same decision in both and a second copy of the form would be a second place to
   * forget that the confirmation is the account's own name. It is rendered beside whichever of
   * the two is on screen, which is why it is built before the detail view's early return.
   */
  const deleteDialog = deleting !== null && (
    <DeleteAccountDialog
      target={deleting}
      serverError={removeUser.error?.message ?? null}
      pending={removeUser.isPending}
      onSubmit={({ confirmDisplayName }) =>
        removeUser.mutate({ userId: deleting.id, confirmDisplayName })
      }
      onClose={() => setDeleting(null)}
    />
  );

  if (openUserId !== null) {
    return (
      <>
        <UserDetailPanel
          userId={openUserId}
          canDelete={canDelete}
          onBack={() => setOpenUserId(null)}
          onDelete={setDeleting}
        />
        {deleteDialog}
      </>
    );
  }

  const items = page.data?.items ?? [];
  const total = page.data?.total ?? 0;
  const shownFrom = total === 0 ? 0 : offset + 1;
  const shownTo = offset + items.length;
  const unreachableHosts = page.data?.presenceUnreachableHosts ?? 0;
  const filtered =
    search !== '' ||
    filters.role !== 'any' ||
    filters.accountType !== 'any' ||
    filters.banned !== 'any' ||
    filters.activity !== 'any';

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Users
        </h2>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            applySearch(draft.trim());
          }}
        >
          <Input
            type="search"
            className="h-9 w-48 sm:w-64"
            placeholder="Email or display name"
            aria-label="Search accounts"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button type="submit" variant="outline" size="sm">
            Search
          </Button>
          {search !== '' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDraft('');
                applySearch('');
              }}
            >
              Clear
            </Button>
          )}
        </form>
      </div>

      <div className="flex flex-wrap items-end gap-2" data-testid="user-filters">
        <FilterSelect
          label="Role"
          value={filters.role}
          options={ROLE_FILTERS}
          onChange={(value) => applyFilter('role', value as Filters['role'])}
        />
        <FilterSelect
          label="Account"
          value={filters.accountType}
          options={TYPE_FILTERS}
          onChange={(value) => applyFilter('accountType', value as Filters['accountType'])}
        />
        <FilterSelect
          label="Bans"
          value={filters.banned}
          options={BAN_FILTERS}
          onChange={(value) => applyFilter('banned', value as Filters['banned'])}
        />
        <FilterSelect
          label="Activity"
          value={filters.activity}
          options={ACTIVITY_FILTERS}
          onChange={(value) => applyFilter('activity', value as Filters['activity'])}
        />
        <label className="block text-xs">
          <span className="mb-1 block font-medium text-muted-foreground">Per page</span>
          <select
            className="h-9 w-24 rounded-lg border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Rows per page"
            value={limit}
            onChange={(event) => {
              setLimit(Number(event.target.value));
              setOffset(0);
            }}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        {filtered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilters(DEFAULT_FILTERS);
              setDraft('');
              applySearch('');
            }}
          >
            Reset filters
          </Button>
        )}
      </div>

      {/* The presence view is a fan-out to every host, so a host that did not answer is a hole
          in it. Said out loud rather than swallowed: its players are drawn as offline, and an
          operator reading "nobody is playing" deserves to know the fleet was not fully asked. */}
      {unreachableHosts > 0 && (
        <p className="text-xs text-muted-foreground">
          {unreachableHosts} host{unreachableHosts === 1 ? '' : 's'} did not answer, so anyone
          playing on {unreachableHosts === 1 ? 'it' : 'them'} is shown as offline.
        </p>
      )}

      {/* The account is gone, so the two things only the deletion itself could say are said
          here rather than on a row that no longer exists. `allowlisted` is the one that matters:
          an operator who read "deleted" and nothing else would be wrong about this address. */}
      {deleted !== null && (
        <div
          className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/40 p-3 text-sm"
          role="status"
          data-testid="deleted-account-notice"
        >
          <div className="space-y-1">
            <p className="font-medium">Deleted {deleted.displayName}.</p>
            {deleted.allowlisted && (
              <p className="text-destructive">
                {deleted.email} is still in ADMIN_EMAILS, so the next sign-in from it creates a
                fresh admin account with no stats and no matches. Take it off the variable if you
                mean it to stay gone.
              </p>
            )}
            {deleted.wasGuest && (
              <p className="text-muted-foreground">
                That was a guest row, reclaimed by a device id anybody can send — the player is one
                refresh away from a different guest row.
              </p>
            )}
            {deleted.unreachableHosts > 0 && (
              <p className="text-muted-foreground">
                {deleted.unreachableHosts} host
                {deleted.unreachableHosts === 1 ? ' did' : 's did'} not answer the disconnect. The
                account is gone either way, and their ticket no longer resolves.
              </p>
            )}
            {deleted.imagesOrphaned > 0 && (
              <p className="text-muted-foreground">
                {deleted.imagesOrphaned} uploaded object
                {deleted.imagesOrphaned === 1 ? '' : 's'} could not be removed from the bucket.
                Nothing serves them any more.
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setDeleted(null)}>
            Dismiss
          </Button>
        </div>
      )}

      {page.error && (
        <p className="text-sm text-destructive" role="alert">
          {page.error.message}
        </p>
      )}
      {setRole.error && (
        <p className="text-sm text-destructive" role="alert">
          {setRole.error.message}
        </p>
      )}
      {liftBan.error && (
        <p className="text-sm text-destructive" role="alert">
          {liftBan.error.message}
        </p>
      )}

      <Card>
        <CardContent className="p-4">
          {page.isLoading ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {filtered ? 'No account matches those filters.' : 'No accounts yet.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    {SORTABLE_COLUMNS.map((column) => (
                      <SortHeader
                        key={column.key}
                        label={column.label}
                        note={'note' in column ? column.note : undefined}
                        active={sort.key === column.key}
                        direction={sort.direction}
                        onSort={() => applySort(column.key)}
                      />
                    ))}
                    {/* Not sortable, and there is nothing under it to sort by: the cell holds a
                        Ban or a Lift, which are actions rather than a value. */}
                    <th className="py-2 pr-3 font-medium">Moderation</th>
                    {/* Deleting is not moderation — it is an admin deciding the account should
                        not exist — so it gets its own column rather than sitting under a heading
                        a moderator's job is named after, and it is drawn for nobody else. */}
                    {canDelete && <th className="py-2 font-medium">Delete</th>}
                  </tr>
                </thead>
                <tbody data-testid="user-rows">
                  {items.map((row) => (
                    <UserRowCells
                      key={row.id}
                      row={row}
                      pending={setRole.isPending}
                      liftPending={liftBan.isPending}
                      canDelete={canDelete}
                      onOpen={() => setOpenUserId(row.id)}
                      onSetRole={(role) => setRole.mutate({ userId: row.id, role })}
                      onBan={() => setBanning(row)}
                      onLift={(banId) => liftBan.mutate({ banId })}
                      onDelete={() => setDeleting(row)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
        <span data-testid="users-range">
          {total === 0 ? 'No accounts' : `${shownFrom}–${shownTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || page.isFetching}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={shownTo >= total || page.isFetching}
            onClick={() => setOffset(offset + limit)}
          >
            Next
          </Button>
        </div>
      </div>

      {banning && (
        <BanDialog
          target={banning}
          serverError={banUser.error?.message ?? null}
          pending={banUser.isPending}
          onSubmit={({ duration, reason }) =>
            banUser.mutate({ userId: banning.id, duration, reason })
          }
          onClose={() => setBanning(null)}
        />
      )}

      {deleteDialog}
    </section>
  );
}

/**
 * One sortable column heading: a button that points the server's ORDER BY at this column.
 *
 * `aria-sort` is on the `<th>` rather than on the button because that is where the attribute is
 * defined to live — a screen reader announces the column's state as it reads the header cell —
 * and the arrow beside the label is `aria-hidden`, since it would otherwise be read out as a
 * character with no meaning. An inactive column carries `none` rather than nothing at all, which
 * is what tells a reader the *table* is sortable and this column is simply not the one in force.
 */
function SortHeader({
  label,
  note,
  active,
  direction,
  onSort,
}: {
  label: string;
  /** A word about what this column is actually ordered by, when the cell draws something else. */
  note?: string;
  active: boolean;
  /** The direction in force. Only meaningful while `active`. */
  direction: AdminUserSortDirection;
  onSort: () => void;
}) {
  return (
    <th
      className="py-2 pr-3 font-medium"
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onSort}
      >
        {label}
        {note !== undefined && <span className="normal-case font-normal">({note})</span>}
        {/* Hidden from a screen reader: `aria-sort` above already says which way this column is
            pointing, and an arrow read aloud is a character rather than an answer. */}
        <span aria-hidden className={active ? '' : 'opacity-30'}>
          {active && direction === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium text-muted-foreground">{label}</span>
      <select
        className="h-9 rounded-lg border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function UserRowCells({
  row,
  pending,
  liftPending,
  canDelete,
  onOpen,
  onSetRole,
  onBan,
  onLift,
  onDelete,
}: {
  row: AdminUserRow;
  pending: boolean;
  liftPending: boolean;
  /** Admin only, and never inferred here: the caller is what knows the viewer's role. */
  canDelete: boolean;
  onOpen: () => void;
  onSetRole: (role: Role) => void;
  onBan: () => void;
  onLift: (banId: string) => void;
  onDelete: () => void;
}) {
  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="py-2 pr-3">
        {/* A button rather than a row-wide click handler: the row already holds a dropdown and
            two buttons, and a click target that swallowed those would be the worse trade. */}
        <button
          type="button"
          className="font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onOpen}
        >
          {row.displayName}
        </button>
        <div className="break-all text-xs text-muted-foreground">{row.email ?? 'No email'}</div>
      </td>
      <td className="py-2 pr-3 text-muted-foreground">{row.isGuest ? 'Guest' : 'Registered'}</td>
      <td className="py-2 pr-3">
        <PresenceCell row={row} />
      </td>
      {/* A rung smaller than the rest of the row: the date carries a time now, and the account
          it belongs to is what a reader is scanning for. */}
      <td className="py-2 pr-3">
        <Instant className="text-xs text-muted-foreground" iso={row.createdAt} />
      </td>
      <td className="py-2 pr-3">
        {row.isGuest ? (
          <span className="text-xs text-muted-foreground">
            Guests cannot hold a role until they register.
          </span>
        ) : (
          <>
            <select
              className="h-9 w-36 rounded-lg border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={`Role for ${row.displayName}`}
              value={row.role}
              disabled={pending}
              onChange={(event) => onSetRole(event.target.value as Role)}
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              {/* A row whose column holds something this build does not know about still shows
                  what it is, rather than silently reading as the first option. */}
              {ROLE_OPTIONS.every((option) => option.value !== row.role) && (
                <option value={row.role}>{row.role}</option>
              )}
            </select>
            {row.allowlisted && (
              <p className="mt-1 max-w-52 text-xs text-muted-foreground">
                In ADMIN_EMAILS; will be granted admin again on next sign-in.
              </p>
            )}
          </>
        )}
      </td>
      <td className="py-2 pr-3">
        {row.activeBan === null ? (
          /* Drawn for every row, admins included: the server is what refuses to ban an admin,
             and it says why in a sentence worth reading. A button that was simply missing
             would leave an operator wondering whether the console was broken. */
          <Button variant="outline" size="sm" onClick={onBan} aria-label={`Ban ${row.displayName}`}>
            Ban
          </Button>
        ) : (
          <div className="space-y-1">
            <span className="block text-xs font-medium text-destructive">Banned</span>
            <Button
              variant="outline"
              size="sm"
              disabled={liftPending}
              aria-label={`Lift the ban on ${row.displayName}`}
              onClick={() => {
                if (row.activeBan !== null) onLift(row.activeBan.id);
              }}
            >
              Lift
            </Button>
          </div>
        )}
      </td>
      {/* Drawn for every row an admin can see, admins included: the server is what refuses to
          delete an admin, and it says why in a sentence worth reading. Never drawn at all for a
          moderator — `admin.users.remove` is `adminProcedure`, and a button that could only ever
          answer FORBIDDEN is a button that lies about what the console is for. */}
      {canDelete && (
        <td className="py-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            aria-label={`Delete ${row.displayName}`}
          >
            Delete
          </Button>
        </td>
      )}
    </tr>
  );
}

/**
 * "Playing now" against "last seen", drawn as the two different facts they are.
 *
 * Online is a live socket; the date under it is `last_seen_at`, which is throttled to one write
 * per five minutes and so can be minutes old for somebody who is very much here. Showing only
 * one of them would make one of those two readings wrong.
 */
function PresenceCell({ row }: { row: AdminUserRow }) {
  return (
    <span className="inline-flex flex-col">
      {row.presence === null ? (
        <Instant className="text-xs text-muted-foreground" iso={row.lastSeenAt} />
      ) : (
        <>
          <span className="inline-flex items-center gap-1.5 font-medium text-emerald-600 dark:text-emerald-400">
            <span aria-hidden className="h-2 w-2 rounded-full bg-current" />
            {row.presence.spectator ? 'Watching' : 'Playing'}
          </span>
          <span className="text-xs text-muted-foreground">{row.presence.roomId}</span>
        </>
      )}
      <LastAddress row={row} />
    </span>
  );
}

/**
 * The address this account last signed in from.
 *
 * **IN THE ACTIVITY CELL RATHER THAN IN A COLUMN OF ITS OWN, AND THAT IS A WIDTH DECISION AS MUCH
 * AS A GROUPING ONE.** This table already carries five sortable columns plus Moderation plus
 * Delete and scrolls sideways on anything but a wide screen; an eighth would push the two action
 * columns off the right for every operator on a laptop. It also belongs here on its merits: this
 * is the where-and-when cell, so the room somebody is in, when they were last seen and where they
 * came from are three answers to one question rather than three columns.
 *
 * **IT IS NOT SORTABLE, AND THAT IS NOT AN OMISSION.** `SORTABLE_COLUMNS` is every field
 * `admin.users.list` accepts as an ORDER BY, and this is not one of them: it is a `DISTINCT ON`
 * over the session tables computed *after* the page — `getOnlinePresence`'s arrangement, and its
 * limit. Ordering by it would mean ranking every account in the deployment before paging, which
 * is the OFFSET page's own argument turned inside out.
 *
 * **IT SAYS "signed in from" RATHER THAN "last IP", because those are different facts.** Nothing
 * in this product records an address per request; a session row records where the account signed
 * in, and a label that implied otherwise would have an operator reading a week-old address as a
 * live one. Absent is drawn as nothing at all rather than as a dash — an account that has never
 * signed in on this deployment has no address, which the empty space says as well as a placeholder
 * would and without adding a row of type to every guest.
 */
function LastAddress({ row }: { row: AdminUserRow }) {
  if (row.lastIpAddress === null) return null;
  return (
    <span
      data-testid="user-last-ip"
      className="font-mono text-[11px] text-muted-foreground"
      title="The address this account last signed in from"
    >
      {row.lastIpAddress}
    </span>
  );
}
